import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { getCasinoOpenBets } from '../../services/casinoService'
import LoaderOverlay from '../LoaderOverlay'
import { formatStake, formatBetAmount } from '../../utils/casinoUtils'

export const formatPlaceDate = (val) => {
  if (!val) return ''
  const d = new Date(val)
  if (isNaN(d)) return String(val)
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  const ss = String(d.getSeconds()).padStart(2, '0')
  return `${dd}/${mm}/${d.getFullYear()} ${hh}:${mi}:${ss}`
}

const emptySearch = () => ({ username: '', amountFrom: '', amountTo: '', ip: '' })
const betTime = (b) => new Date(b.created_at || b.createdAt || 0).getTime()

// Tabs: market of the newest bet first, then the rest by each market's latest bet.
const marketsByLatestBet = (bets, marketOf) => {
  const seen = []
  for (const b of [...bets].sort((a, c) => betTime(c) - betTime(a))) {
    const m = marketOf(b)
    if (m && !seen.includes(m)) seen.push(m)
  }
  return seen
}

const isDeleted = (b) => ['deleted', 'DELETED'].includes(b.status)
const betSide = (b) => String(b.game_type || b.type || b.bet_type || '').toLowerCase()
const isLay = (b) => ['lay', 'no'].includes(betSide(b))

const currency = (v) => {
  const n = parseFloat(v)
  return Number.isFinite(n) ? n.toLocaleString('EN-IN', { maximumFractionDigits: 2 }) : ''
}

// "View More" link of the admin My Bets card + its modal (__BVID__1920) with full
// bet details. Was inside CasinoMyBetsSidebar; split out so pages with their own
// My Bets card (Mini SuperOver) get the same working button.
// nationOf(bet) → the bet name; formatOdds(bet) → the rate cell.
// marketOf(bet) → Super Over family only: original View More is tabbed by
// Bookmaker / Fancy / Fancy1, with the sports search form. Tabs only appear
// for a market that has a bet (user screenshot 2026-10-18).
export default function CasinoViewMore({
  gameName = '',
  nationOf = (bet) => bet.selection || bet.player_name || '',
  formatOdds = (bet) => formatStake(bet.odds ?? bet.rate),
  marketOf = null,
}) {
  const [showViewMore, setShowViewMore] = useState(false)
  const [activeMarket, setActiveMarket] = useState(null)
  const [search, setSearch] = useState(emptySearch)
  const [applied, setApplied] = useState(emptySearch)
  const [radio, setRadio] = useState('all')
  const [modalEntered, setModalEntered] = useState(false)
  useEffect(() => {
    if (!showViewMore) {
      setModalEntered(false)
      return
    }
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setModalEntered(true)))
    return () => cancelAnimationFrame(raf)
  }, [showViewMore])
  const [allBets, setAllBets] = useState([])
  const [loadingAll, setLoadingAll] = useState(false)
  const [tip, setTip] = useState(null)
  const contentRef = useRef(null)

  const tipRef = (el) => {
    if (!el || !tip || !contentRef.current) return
    const contentRect = contentRef.current.getBoundingClientRect()
    const w = el.offsetWidth
    const cx = tip.linkRect.left + tip.linkRect.width / 2
    let x = Math.round(cx - contentRect.left - w / 2)
    x = Math.max(8, Math.min(x, contentRect.width - w - 8))
    const y = Math.round(tip.linkRect.bottom - contentRect.top)
    el.style.transform = `translate3d(${x}px, ${y}px, 0px)`
  }

  const tabMarkets = useMemo(() => {
    if (!marketOf) return null
    return marketsByLatestBet(allBets, marketOf)
  }, [marketOf, allBets])

  useEffect(() => {
    if (!tabMarkets || !tabMarkets.length) return
    if (!tabMarkets.includes(activeMarket)) setActiveMarket(tabMarkets[0])
  }, [tabMarkets, activeMarket])

  const resetFilters = () => {
    setSearch(emptySearch())
    setApplied(emptySearch())
    setRadio('all')
  }

  // Loader on, fetch open bets (oldest first), loader off — on open and on every market tab switch.
  const loadBets = (then) => {
    setLoadingAll(true)
    Promise.all([
      getCasinoOpenBets(gameName)
        .then((res) => (res?.success && Array.isArray(res?.data) ? res.data : []))
        .catch(() => []),
      new Promise((r) => setTimeout(r, 1000)),
    ])
      .then(([list]) => {
        const sorted = [...list].sort((a, b) => betTime(a) - betTime(b))
        setAllBets(sorted)
        then(sorted)
      })
      .finally(() => setLoadingAll(false))
  }

  const handleViewMore = (e) => {
    e?.preventDefault()
    if (loadingAll) return
    loadBets((sorted) => {
      resetFilters()
      if (marketOf) {
        setActiveMarket(marketsByLatestBet(sorted, marketOf)[0] || null)
      } else {
        setActiveMarket(null)
      }
      setShowViewMore(true)
    })
  }

  const listedBets = useMemo(() => {
    const pool = marketOf
      ? allBets.filter((bet) => marketOf(bet) === activeMarket)
      : allBets
    return pool.filter((bet) => {
      const u = String(bet.username || bet.user_name || bet.user_id || '').toLowerCase()
      if (applied.username && !u.includes(applied.username.toLowerCase())) return false
      const amt = parseFloat(bet.amount ?? bet.stake) || 0
      if (applied.amountFrom && amt < parseFloat(applied.amountFrom)) return false
      if (applied.amountTo && amt > parseFloat(applied.amountTo)) return false
      const ip = String(bet.ip_address || bet.ip || '')
      if (applied.ip && !ip.includes(applied.ip)) return false
      if (radio === 'back' && isLay(bet)) return false
      if (radio === 'lay' && !isLay(bet)) return false
      if (radio === 'deleted' && !isDeleted(bet)) return false
      return true
    })
  }, [allBets, marketOf, activeMarket, applied, radio])

  const totalSoda = listedBets.length
  const totalAmount = listedBets.reduce((s, b) => s + (parseFloat(b.amount ?? b.stake) || 0), 0)

  const onSearch = (e) => {
    e.preventDefault()
    setApplied({ ...search })
  }
  const onReset = () => resetFilters()

  const switchMarket = (m) => {
    if (loadingAll) return
    setActiveMarket(m)
    resetFilters()
    loadBets(() => {})
  }

  const close = () => {
    setShowViewMore(false)
    resetFilters()
  }

  const formAndTable = (
    <div>
      {/* Same grid as the sports View More (components/ViewMoreBetsModal.jsx). */}
      <form method="post" className="ajaxFormSubmit" onSubmit={onSearch}>
        <div className="row row5 align-items-center mb-3">
          <div className="col-xl-2">
            <label htmlFor="casino-vm-uname">Username</label>
            <input
              id="casino-vm-uname"
              type="text"
              placeholder="Search Username"
              className="form-control"
              value={search.username}
              onChange={(e) => setSearch((s) => ({ ...s, username: e.target.value }))}
            />
          </div>
          <div className="col-xl-3">
            <div className="row row5">
              <div className="col-6">
                <label htmlFor="casino-vm-amountfrom">Amount From</label>
                <input
                  id="casino-vm-amountfrom"
                  type="text"
                  placeholder="Amount From"
                  className="form-control"
                  value={search.amountFrom}
                  onChange={(e) => setSearch((s) => ({ ...s, amountFrom: e.target.value }))}
                />
              </div>
              <div className="col-6">
                <label htmlFor="casino-vm-amountto">Amount To</label>
                <input
                  id="casino-vm-amountto"
                  type="text"
                  placeholder="Amount To"
                  className="form-control"
                  value={search.amountTo}
                  onChange={(e) => setSearch((s) => ({ ...s, amountTo: e.target.value }))}
                />
              </div>
            </div>
          </div>
          <div className="col-xl-2">
            <label htmlFor="casino-vm-ip">IP Address</label>
            <input
              id="casino-vm-ip"
              type="text"
              placeholder="IP Address"
              className="form-control"
              value={search.ip}
              onChange={(e) => setSearch((s) => ({ ...s, ip: e.target.value }))}
            />
          </div>
          <div className="col-xl-2 mt-4">
            <button type="submit" className="btn btn-primary">Search</button>
            {' '}
            <button type="button" className="btn btn-light" onClick={onReset}>Reset</button>
          </div>
        </div>
      </form>
      <div id="casino-vm-radio" role="radiogroup" tabIndex="-1" className="float-left bv-no-focus-ring">
        {[['all', 'All'], ['back', 'Back'], ['lay', 'Lay'], ['deleted', 'Deleted']].map(([v, label], i) => (
          <div key={v} className="custom-control custom-control-inline custom-radio">
            <input
              id={`casino-vm-radio_${i}`}
              type="radio"
              name="casino-vm-radio"
              className="custom-control-input"
              value={v}
              checked={radio === v}
              onChange={() => setRadio(v)}
            />
            <label htmlFor={`casino-vm-radio_${i}`} className="custom-control-label"><span>{label}</span></label>
          </div>
        ))}
      </div>
      <div className="custom-control-inline float-right">
        <h5>
          Total Soda: <span className="text-success mr-2">{totalSoda}</span> Total Amount:
          {' '}<span className={Number(totalAmount) < 0 ? 'text-danger' : 'text-success'}>{currency(totalAmount)}</span>
        </h5>
      </div>
      <div className="clearfix"></div>
      {listedBets.length === 0 ? (
        <div className="table-responsive report-table text-center">No records found</div>
      ) : (
        <div className="table-responsive report-table">
          <table className="table">
            <thead>
              <tr>
                <th className="user-name"><div>Username</div></th>
                <th className="event-name"><div>Nation</div></th>
                <th className="text-right bet-user-rate"><div>Rate</div></th>
                <th className="text-right bet-amount"><div>Amount</div></th>
                <th className="bet-date"><div>Date</div></th>
                <th><div>IP</div></th>
                <th><div>B Details</div></th>
                <th className="text-right"><div>Action</div></th>
              </tr>
            </thead>
            <tbody>
              {listedBets.map((bet, i) => (
                <tr key={bet.id || bet._id || i} className={isLay(bet) ? 'lay-border' : 'back-border'}>
                  <td className="user-name"><div>{bet.username || bet.user_name || bet.user_id || ''}</div></td>
                  <td className="event-name"><div>{nationOf(bet)}</div></td>
                  <td className="text-right bet-user-rate"><div>{formatOdds(bet)}</div></td>
                  <td className="text-right bet-amount"><div>{currency(bet.amount ?? bet.stake)}</div></td>
                  <td className="bet-date"><div>{formatPlaceDate(bet.created_at || bet.createdAt)}</div></td>
                  <td><a href="javascript:void(0)">{bet.ip_address || bet.ip || ''}</a></td>
                  <td>
                    <a
                      href="#"
                      className="text-success"
                      onClick={(e) => e.preventDefault()}
                      {...(tip ? { 'aria-describedby': '__bv_tooltip_1535__' } : {})}
                      onMouseEnter={(e) => setTip({ text: bet.browser || 'No details available', linkRect: e.currentTarget.getBoundingClientRect() })}
                      onMouseLeave={() => setTip(null)}
                    >Detail</a>
                  </td>
                  <td className="text-right">
                    <div className="custom-control custom-checkbox">
                      <input type="checkbox" className="custom-control-input" value="0" id={`casino-vm-chk-${bet.id || i}`} />
                      <label className="custom-control-label" htmlFor={`casino-vm-chk-${bet.id || i}`}></label>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )

  const diamondTable = (
    <div className="m-t-20 view-more-modal">
      <div id="matched-bet2" className="tab-pane active">
        <div className="table-responsive">
          <table className="table table-diamond table-bordered">
            <thead>
              <tr>
                <th>No</th>
                <th>UserName</th>
                <th>Nation</th>
                <th className="text-right">Amount</th>
                <th className="text-right">User Rate</th>
                <th>Place Date</th>
                <th>IP</th>
                <th>Browser Details</th>
              </tr>
            </thead>
            <tbody>
              {listedBets.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center">No records found</td>
                </tr>
              ) : (
                listedBets.map((bet, i) => (
                  <tr key={bet.id || bet._id || i} className={isLay(bet) ? 'lay-border' : 'back-border'}>
                    <td>{i + 1}</td>
                    <td><span>{bet.username || bet.user_name || bet.user_id || ''}</span></td>
                    <td>{nationOf(bet)}</td>
                    <td className="text-right">{formatBetAmount(bet.amount ?? bet.stake)}</td>
                    <td className="text-right">{formatOdds(bet)}</td>
                    <td>{formatPlaceDate(bet.created_at || bet.createdAt)}</td>
                    <td>{bet.ip_address || ''}</td>
                    <td>
                      <a
                        href="#"
                        className="text-success"
                        onClick={(e) => e.preventDefault()}
                        {...(tip ? { 'aria-describedby': '__bv_tooltip_1535__' } : {})}
                        onMouseEnter={(e) => setTip({ text: bet.browser || 'No details available', linkRect: e.currentTarget.getBoundingClientRect() })}
                        onMouseLeave={() => setTip(null)}
                      >Detail</a>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  return (
    <>
      <a href="#" className="btn btn-back float-right" onClick={handleViewMore}>View More</a>
      {loadingAll && createPortal(<LoaderOverlay />, document.body)}
      {showViewMore && createPortal(
        <>
          <div className={`modal-backdrop fade${modalEntered ? ' show' : ''}`}></div>
          <div
            id="__BVID__1920"
            role="dialog"
            aria-labelledby="__BVID__1920___BV_modal_title_"
            aria-describedby="__BVID__1920___BV_modal_body_"
            className={`modal fade${modalEntered ? ' show' : ''}`}
            aria-modal="true"
            style={{ display: 'block' }}
            onClick={close}
          >
            <div className="modal-dialog modal-big" onClick={(e) => e.stopPropagation()}>
              <span tabIndex="0"></span>
              <div id="__BVID__1920___BV_modal_content_" tabIndex="-1" className="modal-content" ref={contentRef}>
                <header id="__BVID__1920___BV_modal_header_" className="modal-header">
                  <h5 id="__BVID__1920___BV_modal_title_" className="modal-title">View More</h5>
                  <button type="button" aria-label="Close" className="close" onClick={close}>
                    &times;
                  </button>
                </header>
                <div id="__BVID__1920___BV_modal_body_" className="modal-body">
                  {marketOf ? (
                    <>
                      {tabMarkets && tabMarkets.length > 0 && (
                        <ul role="tablist" className="nav nav-tabs d-inline-block text-uppercase">
                          {tabMarkets.map((m) => (
                            <li key={m} className="nav-item d-inline-block">
                              <a
                                data-toggle="tab"
                                href="javascript:void(0)"
                                className={`nav-link${activeMarket === m ? ' active' : ''}`}
                                onClick={(e) => { e.preventDefault(); switchMarket(m) }}
                              >{m}</a>
                            </li>
                          ))}
                        </ul>
                      )}
                      {allBets.length === 0
                        ? <div className="text-center m-t-20">No records found</div>
                        : (
                          // tab-pane: bordered, padded box (style.css `.modal-body .tab-content > .tab-pane`), as in ViewMoreBetsModal
                          <div className="tab-content m-t-20">
                            <div className="tab-pane card-body active">{formAndTable}</div>
                          </div>
                        )}
                    </>
                  ) : diamondTable}
                </div>
                {tip && (
                  <div
                    id="__bv_tooltip_1535__"
                    role="tooltip"
                    tabIndex="-1"
                    ref={tipRef}
                    className="tooltip b-tooltip bs-tooltip-bottom show"
                    x-placement="bottom"
                    style={{ position: 'absolute', willChange: 'transform', top: '0px', left: '0px' }}
                  >
                    <div className="arrow" style={{ left: '90px' }}></div>
                    <div className="tooltip-inner">{tip.text}</div>
                  </div>
                )}
              </div>
              <span tabIndex="0"></span>
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  )
}
